import * as Moment from 'moment'
import { extendMoment } from 'moment-range'
import PortalInstructorAvailabilities from '@classes/InstructorAvailabilities'
import PortalInstructorAvailability from '@classes/InstructorAvailability'
import PortalInstructorUnAvailabilities from '@classes/InstructorUnAvailabilities'
import PortalInstructorUnAvailability from '@classes/InstructorUnAvailability'
import { flatten, isEmpty, sortBy } from 'lodash'
import { DATE_FORMAT } from '@utils/constants'
import { convertMomentToDate, getFilteredBulkDeletePayload } from '@utils/helpers'
import PortalInstructor from './Instructor'
import { TEventResource } from '@components/InstructorScheduler/interface'
import {
  FormValueProps as BulkFormProps,
  IAvailabilityBulkPayload,
  IBulkDeleteUnavailabilityPayload,
} from '@components/Modal/AvailabilityBulkDeleteModal/interface'
import { FormValueProps, IPutAvailabilities, IPutUnAvailabilities } from '@components/Modal/AvailabilityModal/interface'
import omitDeep from '@utils/omit-deep'
import { REPEAT_OPTIONS } from '@utils/constants/availability'

const moment = extendMoment(Moment)

type TAvailabilities = PortalInstructorAvailabilities | null
type TUnAvailabilities = PortalInstructorUnAvailabilities | null

class PortalAvailabilityCalendar {
  availabilities: TAvailabilities
  unAvailabilities: TUnAvailabilities
  Instructor: PortalInstructor
  currentDate: Date
  isAdmin: boolean
  TimeOffConflicts: PortalInstructorUnAvailability[]
  AvailabilityConflicts: PortalInstructorAvailability[]

  constructor(
    availabilities: TAvailabilities,
    unAvailabilities: TUnAvailabilities,
    Instructor: PortalInstructor,
    currentDate: Date,
    isAdmin: boolean
  ) {
    this.availabilities = availabilities
    this.unAvailabilities = unAvailabilities
    this.Instructor = Instructor
    this.currentDate = currentDate
    this.isAdmin = isAdmin
    this.TimeOffConflicts = []
    this.AvailabilityConflicts = []
  }

  get CurrentDate() {
    return moment(this.currentDate).clone()
  }

  get FilterDateRange() {
    return {
      start: convertMomentToDate(
        this.CurrentDate.clone()
          .startOf('week')
          .subtract(1, 'day')
          .startOf('day')
      ),
      end: convertMomentToDate(
        this.CurrentDate.clone()
          .endOf('week')
          .add(1, 'day')
          .endOf('day')
      ),
    }
  }

  get CalendarEvents() {
    return [...this.AvailabilityEvents, ...this.UnAvailabilityEvents]
  }

  get AvailabilityEvents() {
    return flatten(this.OverlappedAvailabilities.map(({ CalendarEvents }) => CalendarEvents))
  }

  get FilteredAvailabilities() {
    return this.availabilities?.filterByDateRange(this.FilterDateRange.start, this.FilterDateRange.end) || []
  }

  get AvailabilitySplits() {
    let splitAvails: PortalInstructorAvailability[] = []

    this.FilteredAvailabilities.map(avail => {
      const timeOffsInsideAvail = this.FilteredUnAvailabilities.filter(({ CalendarStart, CalendarEnd }) => {
        const availRange = moment.range(avail.CalendarStart, avail.CalendarEnd)
        const unAvailRange = moment.range(CalendarStart, CalendarEnd)
        return availRange.contains(unAvailRange) && !availRange.overlaps(unAvailRange)
      })

      splitAvails = [...splitAvails, avail]
      if (timeOffsInsideAvail.length) {
        const schedules: (PortalInstructorAvailability | PortalInstructorUnAvailability)[] = [
          avail,
          ...timeOffsInsideAvail,
        ]

        sortBy(schedules, ({ CalendarStart }) => CalendarStart.valueOf).map((item, index, arr) => {
          const prevItem = (arr[index - 1] || item) as PortalInstructorUnAvailability
          const isLastItem = arr.length === index + 1
          splitAvails = [
            ...splitAvails,
            new PortalInstructorAvailability(
              {
                InstructorAvailabilityInfo: {
                  ...avail,
                  StartTime: !index ? avail.StartTime : moment(prevItem.EndDateTime).format(DATE_FORMAT.HM),
                  EndTime: isLastItem
                    ? avail.EndTime
                    : moment((item as PortalInstructorUnAvailability).StartDateTime).format(DATE_FORMAT.HM),
                },
              },
              this.Instructor
            ),
          ]
        })
      }
    })

    return splitAvails
  }

  get OverlappedAvailabilities() {
    let overlappedAvails: PortalInstructorAvailability[] = []

    this.AvailabilitySplits.map(avail => {
      const start = moment(avail.CalendarStart)
      const end = moment(avail.CalendarEnd)

      const conflicts = this.FilteredUnAvailabilities.filter(({ CalendarStart, CalendarEnd }) => {
        const availRange = moment.range(start, end)
        const unAvailRange = moment.range(moment(CalendarStart), moment(CalendarEnd))
        return availRange.overlaps(unAvailRange) && !unAvailRange.contains(availRange)
      })

      overlappedAvails = [...overlappedAvails, avail]

      if (conflicts.length) {
        const unAvailStart = conflicts.find(({ CalendarStart, CalendarEnd }) => {
          return start.isBetween(CalendarStart, CalendarEnd)
        })
        const unAvailEnd = conflicts.find(({ CalendarStart, CalendarEnd }) => {
          return end.isBetween(CalendarStart, CalendarEnd)
        })

        overlappedAvails = [
          ...overlappedAvails,
          new PortalInstructorAvailability(
            {
              InstructorAvailabilityInfo: {
                ...avail,
                StartTime: unAvailStart ? moment(unAvailStart.CalendarEnd).format(DATE_FORMAT.HM) : avail.StartTime,
                EndTime: unAvailEnd ? moment(unAvailEnd.CalendarStart).format(DATE_FORMAT.HM) : avail.EndTime,
              },
            },
            this.Instructor
          ),
        ]
      }
    })

    return overlappedAvails
  }

  get Availabilities() {
    return this.availabilities?.Availabilities || []
  }

  get UnAvailabilityEvents() {
    return this.FilteredUnAvailabilities.map(({ CalendarEvent }) => CalendarEvent)
  }

  get FilteredUnAvailabilities() {
    return this.unAvailabilities?.filterByDateRange(this.FilterDateRange.start, this.FilterDateRange.end) || []
  }

  get UnAvailabilities() {
    return this.unAvailabilities?.UnAvailabilities || []
  }

  getFilterDateRange(date: Moment.Moment) {
    return {
      start: convertMomentToDate(
        date
          .clone()
          .startOf('week')
          .subtract(1, 'day')
          .startOf('day')
      ),
      end: convertMomentToDate(
        date
          .clone()
          .endOf('week')
          .add(1, 'day')
          .endOf('day')
      ),
    }
  }

  getCalendarEvents(date: Date = new Date()) {
    return [...this.getAvailabilityEvents(date), ...this.getUnAvailabilityEvents(date)]
  }

  getUnAvailabilityEvents(date: Date = new Date()) {
    return this.getFilteredUnAvailabilities(date).map(({ CalendarEvent }) => CalendarEvent)
  }

  getFilteredUnAvailabilities(date: Date = new Date()) {
    const { start, end } = this.getFilterDateRange(moment(date))
    return this.unAvailabilities?.filterByDateRange(start, end) || []
  }

  getAvailabilityEvents(date: Date = new Date()) {
    const { start, end } = this.getFilterDateRange(moment(date))
    const filteredEvents = this.getFilteredAvailabilities({ start, end })
    const splits = this.getAvailabilitySplits(filteredEvents)
    return flatten(this.getOverlappedAvailabilities(splits).map(({ CalendarEvents }) => CalendarEvents))
  }

  getFilteredAvailabilities({ start, end }: { start: Date; end: Date }) {
    return this.availabilities?.filterByDateRange(start, end) || []
  }

  getAvailabilitySplits(availabilities: PortalInstructorAvailability[] = []) {
    let splitAvails: PortalInstructorAvailability[] = []

    availabilities.map(avail => {
      const timeOffsInsideAvail = this.FilteredUnAvailabilities.filter(({ CalendarStart, CalendarEnd }) => {
        const availRange = moment.range(avail.CalendarStart, avail.CalendarEnd)
        const unAvailRange = moment.range(CalendarStart, CalendarEnd)
        return availRange.contains(unAvailRange) && !availRange.overlaps(unAvailRange)
      })

      splitAvails = [...splitAvails, avail]
      if (timeOffsInsideAvail.length) {
        const schedules: (PortalInstructorAvailability | PortalInstructorUnAvailability)[] = [
          avail,
          ...timeOffsInsideAvail,
        ]

        sortBy(schedules, ({ CalendarStart }) => CalendarStart.valueOf).map((item, index, arr) => {
          const prevItem = (arr[index - 1] || item) as PortalInstructorUnAvailability
          const isLastItem = arr.length === index + 1
          splitAvails = [
            ...splitAvails,
            new PortalInstructorAvailability(
              {
                InstructorAvailabilityInfo: {
                  ...avail,
                  StartTime: !index ? avail.StartTime : moment(prevItem.EndDateTime).format(DATE_FORMAT.HM),
                  EndTime: isLastItem
                    ? avail.EndTime
                    : moment((item as PortalInstructorUnAvailability).StartDateTime).format(DATE_FORMAT.HM),
                },
              },
              this.Instructor
            ),
          ]
        })
      }
    })

    return splitAvails
  }

  getOverlappedAvailabilities(availabilitySplits: PortalInstructorAvailability[] = []) {
    let overlappedAvails: PortalInstructorAvailability[] = []

    availabilitySplits.map(avail => {
      const start = moment(avail.CalendarStart)
      const end = moment(avail.CalendarEnd)

      const conflicts = this.FilteredUnAvailabilities.filter(({ CalendarStart, CalendarEnd }) => {
        const availRange = moment.range(start, end)
        const unAvailRange = moment.range(moment(CalendarStart), moment(CalendarEnd))
        return availRange.overlaps(unAvailRange) && !unAvailRange.contains(availRange)
      })

      overlappedAvails = [...overlappedAvails, avail]

      if (conflicts.length) {
        const unAvailStart = conflicts.find(({ CalendarStart, CalendarEnd }) => {
          return start.isBetween(CalendarStart, CalendarEnd)
        })
        const unAvailEnd = conflicts.find(({ CalendarStart, CalendarEnd }) => {
          return end.isBetween(CalendarStart, CalendarEnd)
        })

        overlappedAvails = [
          ...overlappedAvails,
          new PortalInstructorAvailability(
            {
              InstructorAvailabilityInfo: {
                ...avail,
                StartTime: unAvailStart ? moment(unAvailStart.CalendarEnd).format(DATE_FORMAT.HM) : avail.StartTime,
                EndTime: unAvailEnd ? moment(unAvailEnd.CalendarStart).format(DATE_FORMAT.HM) : avail.EndTime,
              },
            },
            this.Instructor
          ),
        ]
      }
    })

    return overlappedAvails
  }

  isEditable(resource: TEventResource) {
    return (
      ((this.isAdmin || !resource?.IsCore) && moment(resource?.CalendarStart).isAfter(this.Instructor.CurrentDate)) ||
      isEmpty(resource)
    )
  }

  isPastDate(date: Date = new Date()) {
    return moment(this.Instructor.CurrentDate).isAfter(date, 'day')
  }

  getBulkDeleteEvents = (values: BulkFormProps): IAvailabilityBulkPayload[] =>
    getFilteredBulkDeletePayload(values, this.Availabilities)

  getDate(date: Moment.Moment, time: Moment.Moment) {
    return moment(
      `${date.clone().format(DATE_FORMAT.BASIC)} ${time.clone().format(DATE_FORMAT.BASIC_TIME)}`,
      DATE_FORMAT.DT
    )
  }

  hasTimeOffConflict = (values: FormValueProps) => {
    return this.TimeOffConflicts.some(({ Id }) => values.Id !== Id)
  }

  hasOverwritables = (values: FormValueProps) => {
    this.AvailabilityConflicts = this.getAvailabilityConflicts(values)
    return this.AvailabilityConflicts.some(
      ({ Type, CalendarTitle }) =>
        values.AvailabilityType.toLowerCase() === Type.toLowerCase() &&
        values.LessonType.toLowerCase() === CalendarTitle?.toLowerCase()
    )
  }

  hasAvailabilityOptionConflicts = (values: FormValueProps) =>
    this.AvailabilityConflicts.some(
      ({ Type, CalendarTitle }) =>
        values.AvailabilityType.toLowerCase() === Type.toLowerCase() &&
        values.LessonType.toLowerCase() !== CalendarTitle?.toLowerCase()
    )

  hasAvailabilityTypeConflicts = (values: FormValueProps) =>
    this.AvailabilityConflicts.some(({ Type }) => values.AvailabilityType.toLowerCase() !== Type.toLowerCase())

  getTimeOffConflicts = (values: FormValueProps) =>
    this.UnAvailabilities.filter(({ CalendarStart, CalendarEnd }) => {
      const start = moment(CalendarStart).tz(this.Instructor.TimeZone)
      const end = moment(CalendarEnd).tz(this.Instructor.TimeZone)

      const formStart = this.getDate(start.clone().tz(this.Instructor.TimeZone), values.StartTime)
      const formEnd = this.getDate(start.clone().tz(this.Instructor.TimeZone), values.EndTime)

      const eventRange = moment.range(convertMomentToDate(start), convertMomentToDate(end))
      const formDateRange = moment.range(
        convertMomentToDate(values.StartDate.clone().startOf('day')),
        convertMomentToDate(values.EndDate.clone().endOf('day'))
      )
      const formRange = moment.range(convertMomentToDate(formStart), convertMomentToDate(formEnd))

      return (
        formDateRange.overlaps(eventRange) &&
        values.RepeatOn.includes(start.weekday()) &&
        formRange.overlaps(eventRange)
      )
    })

  getAvailabilityConflicts = (values: FormValueProps) =>
    this.Availabilities.filter(({ CalendarStart, CalendarEnd, Id }) => {
      const start = moment(CalendarStart).tz(this.Instructor.TimeZone)
      const end = moment(CalendarEnd).tz(this.Instructor.TimeZone)

      const formStart = this.getDate(start.clone().tz(this.Instructor.TimeZone), values.StartTime)
      const formEnd = this.getDate(start.clone().tz(this.Instructor.TimeZone), values.EndTime)

      const eventRange = moment.range(convertMomentToDate(start), convertMomentToDate(end))
      const formDateRange = moment.range(
        convertMomentToDate(values.StartDate.clone().startOf('day')),
        convertMomentToDate(values.EndDate.clone().endOf('day'))
      )
      const formRange = moment.range(convertMomentToDate(formStart), convertMomentToDate(formEnd))

      return (
        formDateRange.overlaps(eventRange) &&
        values.RepeatOn.includes(start.weekday()) &&
        formRange.overlaps(eventRange) &&
        values.Id !== Id
      )
    })

  hasConflicts(isTimeOff: boolean, values: FormValueProps) {
    if (isTimeOff) {
      this.TimeOffConflicts = this.getTimeOffConflicts(values)
      return this.hasTimeOffConflict(values)
    } else {
      this.AvailabilityConflicts = this.getAvailabilityConflicts(values)
      return this.hasAvailabilityTypeConflicts(values) || this.hasAvailabilityOptionConflicts(values)
    }
  }

  getAvailabilityPayload(values?: FormValueProps | null): IPutAvailabilities {
    return {
      FormValues: {
        ...values,
        StartDate: values?.StartDate.format(DATE_FORMAT.BASIC),
        StartTime: values?.StartTime.format(DATE_FORMAT.HM),
        EndDate: values?.EndDate.format(DATE_FORMAT.BASIC),
        EndTime: values?.EndTime.format(DATE_FORMAT.HM),
      },
      Instructor: {
        InstructorProfileId: this.Instructor.Id || '',
        InstructorName: this.Instructor.FullName,
        AvailabilityOption: this.Instructor.AvailabilityOption,
        TimeZone: this.Instructor.TimeZone,
      },
      Availabilities: this.AvailabilityConflicts.map(({ Availability }) => ({
        InstructorAvailabilityInfo: {
          ...omitDeep(Availability, '__typename'),
        },
      })),
    }
  }

  getBulkDeleteUnavailabilityPayload(values?: FormValueProps | null): IBulkDeleteUnavailabilityPayload {
    return {
      StartDate: values?.StartDate.format(DATE_FORMAT.BASIC) || '',
      EndDate: values?.EndDate.format(DATE_FORMAT.BASIC) || '',
      Timezone: this.Instructor.TimeZone,
      InstructorId: this.Instructor.Id || '',
      Types: values?.Type || '',
      WeekDays: values?.RepeatOn.map(index => REPEAT_OPTIONS[index].label).join() || '',
    }
  }

  getUnAvailabilityPayload(values: FormValueProps | null): IPutUnAvailabilities {
    return {
      FormValues: {
        ...values,
        StartDate: values?.StartDate.format(DATE_FORMAT.BASIC),
        StartTime: values?.StartTime.format(DATE_FORMAT.HM),
        EndDate: values?.EndDate.format(DATE_FORMAT.BASIC),
        EndTime: values?.EndTime.format(DATE_FORMAT.HM),
      },
      Instructor: {
        InstructorProfileId: this.Instructor.Id,
        InstructorName: this.Instructor.FullName,
        AvailabilityOption: this.Instructor.AvailabilityOption,
        TimeZone: this.Instructor.TimeZone,
        IsFreelance: this.Instructor.IsFreelance,
      },
    }
  }

  getTimeOffDeletePayload = (resource: TEventResource, isRecurring: boolean) =>
    isRecurring
      ? this.unAvailabilities?.UnAvailabilities.filter(
          ({ UnAvailabilityType, CalendarStart }) =>
            UnAvailabilityType === resource?.CalendarTitle &&
            moment(CalendarStart).isSameOrAfter(resource?.CalendarStart)
        ).map(rs => rs?.getDeletePayload(false))
      : [resource?.getDeletePayload(false)]
}

export default PortalAvailabilityCalendar
